import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X } from "lucide-react";
import SignaturePad from "@/components/SignaturePad";

function hoursBetween(start, end, breakMin) {
    if (!/^\d{2}:\d{2}$/.test(start) || !/^\d{2}:\d{2}$/.test(end)) return 0;
    const [sh, sm] = start.split(":").map(Number);
    const [eh, em] = end.split(":").map(Number);
    let mins = eh * 60 + em - (sh * 60 + sm);
    if (mins < 0) mins += 24 * 60;
    mins -= Math.max(0, breakMin || 0);
    return Math.max(0, Math.round((mins / 60) * 100) / 100);
}

/** Modal used by CANDIDATES to declare their hours after a mission. */
export function CandidateTimesheetModal({ open, application, onClose, onSubmitted }) {
    const m = application?.mission || {};
    const [start, setStart] = useState(m.start_time || "18:00");
    const [end, setEnd] = useState(m.end_time || "23:00");
    const [breakMin, setBreakMin] = useState(0);
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (open) {
            setStart(m.start_time || "18:00");
            setEnd(m.end_time || "23:00");
            setBreakMin(0); setNotes("");
        }
    }, [open, application?.id]);
    if (!open || !application) return null;
    const hours = hoursBetween(start, end, breakMin);
    const submit = async () => {
        if (hours <= 0) { toast.error("Durée invalide"); return; }
        setBusy(true);
        try {
            await api.post("/timesheets", {
                application_id: application.id,
                declared_start: start,
                declared_end: end,
                declared_break_minutes: breakMin,
                declared_notes: notes,
            });
            toast.success("Relevé transmis au client.");
            if (onSubmitted) onSubmitted();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-[#0B192C]/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={onClose} data-testid="candidate-timesheet-modal">
            <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
                 onClick={(e) => e.stopPropagation()}>
                <div className="p-6 border-b border-slate-100 flex justify-between items-start">
                    <div>
                        <div className="overline text-[#F5A623]">Relevé d'heures</div>
                        <h2 className="font-display text-xl mt-1">{m.title}</h2>
                        <div className="text-xs text-slate-500 mt-1">{m.date} · {m.city}</div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-semibold block mb-1">Heure d'arrivée</label>
                            <input type="time" value={start} onChange={(e) => setStart(e.target.value)}
                                   className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                                   data-testid="ts-start" />
                        </div>
                        <div>
                            <label className="text-xs font-semibold block mb-1">Heure de départ</label>
                            <input type="time" value={end} onChange={(e) => setEnd(e.target.value)}
                                   className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                                   data-testid="ts-end" />
                        </div>
                    </div>
                    <div>
                        <label className="text-xs font-semibold block mb-1">Pause (minutes)</label>
                        <input type="number" min={0} max={240} value={breakMin}
                               onChange={(e) => setBreakMin(parseInt(e.target.value || "0"))}
                               className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                               data-testid="ts-break" />
                    </div>
                    <div className="text-sm bg-slate-50 rounded-lg p-3">
                        Total déclaré : <b>{hours.toFixed(2)} h</b>
                    </div>
                    <div>
                        <label className="text-xs font-semibold block mb-1">
                            Observations <span className="text-slate-400 font-normal">(facultatif)</span>
                        </label>
                        <textarea rows={3} maxLength={2000} value={notes}
                                  onChange={(e) => setNotes(e.target.value)}
                                  className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm"
                                  data-testid="ts-notes" />
                    </div>
                </div>
                <div className="p-4 border-t border-slate-100 flex justify-end gap-2 bg-slate-50">
                    <button onClick={onClose} className="btn-ghost-navy text-sm" data-testid="ts-cancel">Annuler</button>
                    <button onClick={submit} disabled={busy || hours <= 0}
                            className="btn-gold text-sm disabled:opacity-50" data-testid="ts-submit">
                        {busy ? "Envoi…" : "Transmettre au client"}
                    </button>
                </div>
            </div>
        </div>
    );
}

/** Modal used by CLIENTS to validate (with signature) or dispute a timesheet. */
export function ClientTimesheetValidationModal({ open, timesheet, onClose, onDone }) {
    const [mode, setMode] = useState("view");  // 'view' | 'sign' | 'dispute'
    const [counterStart, setCounterStart] = useState("");
    const [counterEnd, setCounterEnd] = useState("");
    const [counterBreak, setCounterBreak] = useState(0);
    const [disputeNote, setDisputeNote] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        if (open && timesheet) {
            setMode("view");
            setCounterStart(timesheet.declared_start);
            setCounterEnd(timesheet.declared_end);
            setCounterBreak(timesheet.declared_break_minutes || 0);
            setDisputeNote("");
        }
    }, [open, timesheet?.id]);
    if (!open || !timesheet) return null;
    const m = timesheet.mission || {};
    const c = timesheet.candidate || {};
    const counterHours = hoursBetween(counterStart, counterEnd, counterBreak);
    const dispute = async () => {
        setBusy(true);
        try {
            await api.post(`/timesheets/${timesheet.id}/dispute`, {
                counter_start: counterStart,
                counter_end: counterEnd,
                counter_break_minutes: counterBreak,
                dispute_note: disputeNote,
            });
            toast.success("Contre-proposition envoyée");
            if (onDone) onDone();
            onClose();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur");
        } finally { setBusy(false); }
    };
    return (
        <div className="fixed inset-0 z-50 bg-[#0B192C]/70 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={onClose} data-testid="client-timesheet-modal">
            <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden"
                 onClick={(e) => e.stopPropagation()}>
                <div className="p-6 border-b border-slate-100 flex justify-between items-start">
                    <div>
                        <div className="overline text-[#F5A623]">Relevé d'heures à valider</div>
                        <h2 className="font-display text-lg mt-1">{m.title}</h2>
                        <div className="text-xs text-slate-500 mt-1">
                            {m.date} · {c.first_name} {c.last_name}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
                        <X size={20} />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <div className="bg-slate-50 rounded-lg p-3 text-sm">
                        <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-1 font-bold">
                            Déclaration du prestataire
                        </div>
                        <div>Arrivée : <b>{timesheet.declared_start}</b> · Départ : <b>{timesheet.declared_end}</b></div>
                        <div>Pause : <b>{timesheet.declared_break_minutes || 0} min</b></div>
                        <div>Total : <b>{timesheet.declared_hours?.toFixed(2)} h</b></div>
                        {timesheet.declared_notes && (
                            <div className="mt-2 italic text-slate-600">« {timesheet.declared_notes} »</div>
                        )}
                    </div>

                    {mode === "view" && (
                        <div className="flex flex-col gap-2">
                            <button onClick={() => setMode("sign")}
                                    className="btn-gold text-sm w-full"
                                    data-testid="ts-open-sign">
                                ✅ Valider ces heures et signer
                            </button>
                            <button onClick={() => setMode("dispute")}
                                    className="btn-ghost-navy text-sm w-full"
                                    data-testid="ts-open-dispute">
                                ✏️ Proposer d'autres heures
                            </button>
                        </div>
                    )}

                    {mode === "sign" && (
                        <div className="space-y-2">
                            <div className="text-sm font-semibold">Signature du client</div>
                            <SignaturePad
                                submitLabel={busy ? "Validation…" : "Confirmer la validation"}
                                disabled={busy}
                                onSubmit={async (dataUrl) => {
                                    setBusy(true);
                                    try {
                                        await api.post(`/timesheets/${timesheet.id}/validate`, { signature: dataUrl });
                                        toast.success("Relevé validé");
                                        if (onDone) onDone();
                                        onClose();
                                    } catch (e) {
                                        toast.error(e?.response?.data?.detail || "Erreur");
                                    } finally { setBusy(false); }
                                }}
                            />
                            <button onClick={() => setMode("view")} className="btn-ghost-navy text-sm w-full">
                                Retour
                            </button>
                        </div>
                    )}

                    {mode === "dispute" && (
                        <div className="space-y-2">
                            <div className="text-sm font-semibold">Proposer d'autres heures</div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="text-xs">Arrivée</label>
                                    <input type="time" value={counterStart}
                                           onChange={(e) => setCounterStart(e.target.value)}
                                           className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
                                           data-testid="ts-counter-start" />
                                </div>
                                <div>
                                    <label className="text-xs">Départ</label>
                                    <input type="time" value={counterEnd}
                                           onChange={(e) => setCounterEnd(e.target.value)}
                                           className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
                                           data-testid="ts-counter-end" />
                                </div>
                            </div>
                            <div>
                                <label className="text-xs">Pause (min)</label>
                                <input type="number" value={counterBreak}
                                       onChange={(e) => setCounterBreak(parseInt(e.target.value || "0"))}
                                       className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
                                       data-testid="ts-counter-break" />
                            </div>
                            <div className="text-xs bg-slate-50 rounded p-2">
                                Total révisé : <b>{counterHours.toFixed(2)} h</b>
                            </div>
                            <textarea rows={3} placeholder="Message pour le prestataire (facultatif)"
                                      value={disputeNote} onChange={(e) => setDisputeNote(e.target.value)}
                                      maxLength={2000}
                                      className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-sm"
                                      data-testid="ts-dispute-note" />
                            <button onClick={dispute} disabled={busy || counterHours <= 0}
                                    className="btn-gold text-sm w-full disabled:opacity-50"
                                    data-testid="ts-dispute-btn">
                                {busy ? "Envoi…" : "Envoyer la contre-proposition"}
                            </button>
                            <button onClick={() => setMode("view")} className="btn-ghost-navy text-sm w-full">
                                Retour
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
